// Which language a page is in, and the <head> tags for the pages that aren't
// articles (ArticlePage sets its own).
//
// The URL is the source of truth: /es/... and /pt/... are Spanish and
// Portuguese, the home (/) and /blog/... are English. Only pages without a
// language in their URL (admin, 404) fall back to the visitor's preference:
// what they picked in the selector, else their browser language.

import PAGE_META from './constants/pageMeta.json';
import ABOUT from './constants/aboutPage.json';
import { TEAM_AUTHOR_ID } from './authors';

export const SITE_LOCALES = ['es', 'en', 'pt'];
export const LANGUAGE_STORAGE_KEY = 'marvel_rivals_lang';
export const SITE_URL = 'https://rivalsbase.com';
const OG_LOCALES = { es: 'es_ES', en: 'en_US', pt: 'pt_BR' };
const DEFAULT_SHARE_IMAGE = `${SITE_URL}/og-image.jpg`;

export const homeHref = (locale) => (locale === 'en' ? '/' : `/${locale}`);
export const blogHref = (locale) => (locale === 'en' ? '/blog' : `/${locale}/blog`);
// An article's URL in that locale, or null when it has no slug there.
export const articleHref = (article, locale) => {
  const slug = article?.slug?.[locale];
  if (!slug) return null;
  return locale === 'en' ? `/blog/${slug}` : `/${locale}/blog/${slug}`;
};

export const aboutHref = (locale) => (locale === 'en' ? '/about' : `/${locale}/about`);
export const authorHref = (authorId, locale) => (locale === 'en' ? `/blog/author/${authorId}` : `/${locale}/blog/author/${authorId}`);

export const categoryHref = (category, locale) => {
  const slug = category?.slug?.[locale];
  if (!slug) return null;
  return locale === 'en' ? `/blog/category/${slug}` : `/${locale}/blog/category/${slug}`;
};

// hreflang links: each locale's URL, plus x-default = the English one when
// there is one. Same rule as alternatesFor() in functions/seo/render.js, so
// the tags React writes match the ones the server sent.
const alternatesFor = (locales, hrefFor) => {
  const list = locales.map((locale) => ({ hreflang: locale, href: `${SITE_URL}${hrefFor(locale)}` }));
  if (locales.includes('en')) list.push({ hreflang: 'x-default', href: `${SITE_URL}${hrefFor('en')}` });
  return list;
};

const SAFE_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

// The locales of an article offered to Google: it has a slug and text there,
// and the translation isn't a draft (indexableLocales() in render.js).
export const getIndexableArticleLocales = (article) => SITE_LOCALES.filter((locale) => (
  SAFE_SLUG.test(article?.slug?.[locale] || '')
  && ['title', 'excerpt', 'content'].some((field) => hasText(article?.[field]?.[locale]))
  && article?.translationStatus?.[locale] !== 'draft'
));

// An article's hreflang links, seen from `locale`. None when that locale
// itself isn't offered to Google (a draft translation), like the server.
export const getArticleAlternates = (article, locale) => {
  const locales = getIndexableArticleLocales(article);
  return locales.includes(locale) ? alternatesFor(locales, (alt) => articleHref(article, alt)) : [];
};

// A category's hreflang links: the locales it has a slug in.
export const getCategoryAlternates = (category) => alternatesFor(
  SITE_LOCALES.filter((locale) => SAFE_SLUG.test(category?.slug?.[locale] || '')),
  (locale) => categoryHref(category, locale),
);

// Mirrors the routes in AppRouter.jsx (only es and pt take a prefix: LocaleGate).
const PAGE_PATTERNS = [
  { kind: 'home', pattern: /^\/(es|pt)?\/?$/ },
  { kind: 'blog', pattern: /^(\/(es|pt))?\/blog\/?$/ },
  { kind: 'category', pattern: /^(\/(es|pt))?\/blog\/category\/[^/]+\/?$/ },
  { kind: 'author', pattern: /^(\/(es|pt))?\/blog\/author\/[^/]+\/?$/ },
  { kind: 'about', pattern: /^(\/(es|pt))?\/about\/?$/ },
  { kind: 'article', pattern: /^(\/(es|pt))?\/blog\/[^/]+\/?$/ },
  { kind: 'admin', pattern: /^\/admin(\/|$)/ },
];

export const getPageKind = (pathname) => {
  const found = PAGE_PATTERNS.find(({ pattern }) => pattern.test(pathname));
  return found ? found.kind : 'notFound';
};

// The locale a URL is written in, or null when the URL doesn't say.
export const getUrlLocale = (pathname) => {
  const [first = ''] = pathname.split('/').filter(Boolean);
  if (SITE_LOCALES.includes(first)) return first;
  if (first === '' || first === 'blog' || first === 'about') return 'en';
  return null;
};

export const getPreferredLocale = (saved, browserLanguages = []) => {
  if (SITE_LOCALES.includes(saved)) return saved;
  const browser = browserLanguages
    .map((language) => String(language || '').slice(0, 2).toLowerCase())
    .find((language) => SITE_LOCALES.includes(language));
  return browser || 'en';
};

export const readPreferredLocale = () => {
  let saved = null;
  try {
    saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // Storage blocked: fall back to the browser language.
  }
  const languages = typeof navigator !== 'undefined' ? (navigator.languages || [navigator.language]) : [];
  return getPreferredLocale(saved, languages);
};

export const savePreferredLocale = (locale) => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
  } catch {
    // Storage blocked: the choice only lasts for this visit.
  }
};

// <head> tags for one non-article page. Category pages use the blog's tags
// (BlogLandingPage refines the title once the category has loaded).
export const getPageMeta = (kind, locale) => {
  if (kind === 'about') {
    const about = ABOUT[locale] || ABOUT.en;
    return {
      title: `${about.title} | Rivals Base`,
      description: about.description,
      canonical: `${SITE_URL}${aboutHref(locale)}`,
      alternates: alternatesFor(SITE_LOCALES, aboutHref),
      noindex: false,
      ogLocale: OG_LOCALES[locale] || OG_LOCALES.en,
    };
  }
  const metaKind = kind === 'category' ? 'blog' : kind;
  const meta = PAGE_META[metaKind]?.[locale] || PAGE_META[metaKind]?.en;
  if (!meta) return null;
  const hrefFor = { home: homeHref, blog: blogHref }[kind];
  return {
    title: meta.title,
    description: meta.description || PAGE_META.home[locale]?.description || PAGE_META.home.en.description,
    canonical: hrefFor ? `${SITE_URL}${hrefFor(locale)}` : null,
    alternates: hrefFor ? alternatesFor(SITE_LOCALES, hrefFor) : [],
    noindex: kind === 'notFound' || kind === 'admin',
    ogLocale: OG_LOCALES[locale] || OG_LOCALES.en,
  };
};

// A category page's <head> tags once the category has loaded; the same ones
// buildListPage() in functions/seo/render.js sends. A category with no
// published articles is noindex (and out of the sitemap) until it gets one.
export const getCategoryPageMeta = (category, locale, { empty = false } = {}) => {
  const blog = getPageMeta('blog', locale);
  const text = (field) => String(category?.[field]?.[locale] || '').trim();
  const href = categoryHref(category, locale);
  return {
    ...blog,
    title: text('seoTitle') || `${text('name')}: Marvel Rivals | Rivals Base`,
    description: text('seoDescription') || text('description') || blog.description,
    canonical: href ? `${SITE_URL}${href}` : null,
    alternates: empty ? [] : getCategoryAlternates(category),
    noindex: empty,
  };
};

const authorText = (field, locale) => String(field?.[locale] || field?.en || field?.es || '').trim();

// The first ~160 characters, cut at a word (summarize() in functions/seo/render.js).
export const summarize = (text, max = 160) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > 0 ? cut.slice(0, space) : cut).replace(/[\s.,;:]+$/, '')}…`;
};

// An author page's <head> tags, the same ones authorPageMeta() in
// functions/seo/render.js sends. No published articles = noindex.
export const getAuthorPageMeta = (author, locale, { empty = false } = {}) => {
  const tagline = authorText(author.tagline, locale);
  return {
    ...getPageMeta('blog', locale),
    title: tagline ? `${author.name}: ${tagline} | Rivals Base` : `${author.name} | Rivals Base`,
    description: summarize(authorText(author.bio, locale) || tagline) || getPageMeta('blog', locale).description,
    canonical: `${SITE_URL}${authorHref(author.id, locale)}`,
    alternates: empty ? [] : alternatesFor(SITE_LOCALES, (alt) => authorHref(author.id, alt)),
    noindex: empty,
    ogType: 'profile',
    image: author.avatarUrl || null,
  };
};

// ProfilePage JSON-LD, as authorJsonLd() in functions/seo/render.js.
export const getAuthorJsonLd = (author, locale) => {
  const url = `${SITE_URL}${authorHref(author.id, locale)}`;
  const tagline = authorText(author.tagline, locale);
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    url,
    inLanguage: locale,
    mainEntity: {
      '@type': author.id === TEAM_AUTHOR_ID ? 'Organization' : 'Person',
      name: author.name,
      url,
      ...(tagline ? { description: tagline } : {}),
      ...(author.avatarUrl ? { image: author.avatarUrl } : {}),
      ...((author.socials || []).length ? { sameAs: author.socials.map((social) => social.url) } : {}),
    },
  };
};

// AboutPage JSON-LD, as aboutJsonLd() in functions/seo/render.js.
export const getAboutJsonLd = (locale) => ({
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  url: `${SITE_URL}${aboutHref(locale)}`,
  inLanguage: locale,
  name: ABOUT[locale].heading,
  description: ABOUT[locale].description,
  mainEntity: {
    '@type': 'Organization',
    name: 'Rivals Base',
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    founder: { '@type': 'Person', name: 'Fer Brambi', url: `${SITE_URL}${authorHref('fer-brambi', locale)}` },
  },
});

const PAGE_TAG = 'data-page-metadata';
// What public/index.html (and so every server-rendered page) says on a page
// Google may index: the large image is what Discover needs to show the page.
export const INDEXABLE_ROBOTS = 'max-image-preview:large';

const setMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!content) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

// Structured data for a non-article page; cleared with the page's other
// generated tags when the next page applies its own.
export const applyPageJsonLd = (data) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(PAGE_TAG, 'generated');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return () => script.remove();
};

export const clearPageAlternates = () => {
  document.head.querySelectorAll(`[${PAGE_TAG}="generated"]`).forEach((element) => element.remove());
};

export const applyPageMeta = (meta, locale) => {
  document.documentElement.lang = locale;
  clearPageAlternates();
  if (!meta) return;
  document.title = meta.title;
  setMeta('name', 'description', meta.description);
  setMeta('name', 'robots', meta.noindex ? 'noindex,nofollow' : INDEXABLE_ROBOTS);
  setMeta('property', 'og:title', meta.title);
  setMeta('property', 'og:description', meta.description);
  setMeta('property', 'og:type', meta.ogType || 'website');
  setMeta('property', 'og:locale', meta.ogLocale);
  setMeta('property', 'og:url', meta.canonical);
  setMeta('property', 'og:image', meta.image || DEFAULT_SHARE_IMAGE);
  setMeta('name', 'twitter:title', meta.title);
  setMeta('name', 'twitter:description', meta.description);
  setMeta('name', 'twitter:image', meta.image || DEFAULT_SHARE_IMAGE);
  // The shell's 1200x630 only describes og-image.jpg.
  if (meta.image) {
    setMeta('property', 'og:image:width', null);
    setMeta('property', 'og:image:height', null);
  }

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (meta.canonical) {
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', meta.canonical);
  } else {
    canonical?.remove();
  }

  meta.alternates.forEach(({ hreflang, href }) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    link.setAttribute(PAGE_TAG, 'generated');
    document.head.appendChild(link);
  });
};
